/**
 * Render modal body HTML for frontend view script.
 */
import { __ } from '@wordpress/i18n';
import { MODAL_TYPES } from './constants';
import {
	escapeHtml,
	getModalChromeLabel,
	getModalGridClassName,
	normalizeCapitalModalContent,
	normalizeFundModalContent,
	sanitizeRichHtml,
} from './modal-utils';

/**
 * @param {string}  label         CTA label.
 * @param {string}  url           CTA URL.
 * @param {boolean} opensInNewTab New tab flag.
 * @return {string} CTA HTML.
 */
function renderCta( label, url, opensInNewTab ) {
	if ( ! label ) {
		return '';
	}

	if ( url ) {
		const target = opensInNewTab
			? ' target="_blank" rel="noopener noreferrer"'
			: '';
		return `<a class="alteia-mtm-modal__cta alteia-cta-button" href="${ escapeHtml(
			url
		) }"${ target }><span>${ escapeHtml(
			label
		) }</span><span class="alteia-cta-button__arrow" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></a>`;
	}

	return `<button type="button" class="alteia-mtm-modal__cta alteia-cta-button"><span>${ escapeHtml(
		label
	) }</span><span class="alteia-cta-button__arrow" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></button>`;
}

/**
 * @param {string} html Rich text HTML.
 * @return {string} Rich text block markup.
 */
function renderRichBlock( html ) {
	const sanitized = sanitizeRichHtml( html );

	if ( ! sanitized ) {
		return '';
	}

	return `<div class="alteia-mtm-modal__rich-text">${ sanitized }</div>`;
}

/**
 * @param {Object} card Card modal data.
 * @return {string} Fund modal HTML.
 */
function renderFundModalBody( card ) {
	const content = normalizeFundModalContent( card.modalContent );
	const gridClass = getModalGridClassName( card );

	const listItems = content.keyCharacteristics
		.map( ( item ) => `<li>${ escapeHtml( item ) }</li>` )
		.join( '' );

	const footnote = content.keyCharacteristicsFootnote
		? `<p class="alteia-mtm-modal__footnote"><em>${ escapeHtml(
				content.keyCharacteristicsFootnote
		  ) }</em></p>`
		: '';

	const howItWorksItems = ( content.howItWorksItems || [] )
		.map( ( item ) => `<li>${ escapeHtml( item ) }</li>` )
		.join( '' );

	const howItWorks =
		content.howItWorks || howItWorksItems
			? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--how-it-works"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
					getModalChromeLabel(
						'howItWorks',
						__( 'How it works', 'alteia' )
					)
			  ) }</h3><div class="alteia-mtm-modal__card-body">${
					content.howItWorks
						? renderRichBlock( content.howItWorks )
						: ''
			  }${
					howItWorksItems ? `<ul>${ howItWorksItems }</ul>` : ''
			  }</div></article>`
			: '';

	const availableVehicles = content.availableVehicles
		? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--available-vehicles"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
				getModalChromeLabel(
					'availableVehicles',
					__( 'Available vehicles', 'alteia' )
				)
		  ) }</h3><div class="alteia-mtm-modal__card-body">${ renderRichBlock(
				content.availableVehicles
		  ) }</div></article>`
		: '';

	const keyCharacteristics = listItems
		? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--key-characteristics"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
				getModalChromeLabel(
					'keyCharacteristics',
					__( 'Key characteristics', 'alteia' )
				)
		  ) }</h3><div class="alteia-mtm-modal__card-body"><ul>${ listItems }</ul>${ footnote }</div></article>`
		: '';

	const nextStep =
		content.nextStep || content.ctaLabel
			? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--next-step"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
					getModalChromeLabel(
						'nextStep',
						__( 'Next step', 'alteia' )
					)
			  ) }</h3><div class="alteia-mtm-modal__card-body">${
					content.nextStep ? renderRichBlock( content.nextStep ) : ''
			  }${ renderCta(
					content.ctaLabel,
					content.ctaUrl,
					content.ctaOpensInNewTab
			  ) }</div></article>`
			: '';

	const leftCol =
		howItWorks || availableVehicles
			? `<div class="alteia-mtm-modal__fund-col alteia-mtm-modal__fund-col--left">${ howItWorks }${ availableVehicles }</div>`
			: '';

	const rightCol =
		keyCharacteristics || nextStep
			? `<div class="alteia-mtm-modal__fund-col alteia-mtm-modal__fund-col--right">${ keyCharacteristics }${ nextStep }</div>`
			: '';

	return `<div class="${ gridClass }">
		${
			content.overview
				? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--overview"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
						getModalChromeLabel(
							'overview',
							__( 'Overview', 'alteia' )
						)
				  ) }</h3><div class="alteia-mtm-modal__card-body">${ renderRichBlock(
						content.overview
				  ) }</div></article>`
				: ''
		}
		${ leftCol }
		${ rightCol }
	</div>`;
}

/**
 * @param {Object} card Card modal data.
 * @return {string} Capital modal HTML.
 */
function renderCapitalModalBody( card ) {
	const content = normalizeCapitalModalContent(
		card.modalContent,
		card.modalContentId
	);
	const gridClass = getModalGridClassName( card );

	const tableRows = ( content.fundOverview || [] )
		.map(
			( row ) =>
				`<tr><th scope="row">${ escapeHtml(
					row.label
				) }</th><td>${ escapeHtml( row.value ) }</td></tr>`
		)
		.join( '' );

	const characteristics = ( content.investmentCharacteristics || [] )
		.map( ( item ) => `<li>${ escapeHtml( item ) }</li>` )
		.join( '' );

	return `<div class="${ gridClass }">
		${
			content.aboutTheFund
				? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--about"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
						getModalChromeLabel(
							'aboutTheFund',
							__( 'About the fund', 'alteia' )
						)
				  ) }</h3><div class="alteia-mtm-modal__card-body">${ renderRichBlock(
						content.aboutTheFund
				  ) }</div></article>`
				: ''
		}
		${
			content.investmentObjective
				? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--objective"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
						getModalChromeLabel(
							'investmentObjective',
							__( 'Investment objective', 'alteia' )
						)
				  ) }</h3><div class="alteia-mtm-modal__card-body">${ renderRichBlock(
						content.investmentObjective
				  ) }</div></article>`
				: ''
		}
		${
			tableRows
				? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--overview-table"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
						getModalChromeLabel(
							'fundOverview',
							__( 'Fund overview', 'alteia' )
						)
				  ) }</h3><div class="alteia-mtm-modal__card-body"><div class="alteia-mtm-modal__table-wrap" tabindex="0" role="region" aria-label="${ escapeHtml(
						getModalChromeLabel(
							'fundOverviewTable',
							__( 'Fund overview table', 'alteia' )
						)
				  ) }"><table class="alteia-mtm-modal__table"><tbody>${ tableRows }</tbody></table></div></div></article>`
				: ''
		}
		<div class="alteia-mtm-modal__capital-pair">
			${
				characteristics
					? `<article class="alteia-mtm-modal__card alteia-mtm-modal__card--characteristics"><h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
							getModalChromeLabel(
								'investmentCharacteristics',
								__( 'Investment characteristics', 'alteia' )
							)
					  ) }</h3><div class="alteia-mtm-modal__card-body"><ul>${ characteristics }</ul></div></article>`
					: ''
			}
			<article class="alteia-mtm-modal__card alteia-mtm-modal__card--interested">
				<h3 class="alteia-mtm-modal__card-title">${ escapeHtml(
					content.interestedHeading
				) }</h3>
				<div class="alteia-mtm-modal__card-body">
					${ content.interestedText ? renderRichBlock( content.interestedText ) : '' }
					${ renderCta( content.ctaLabel, content.ctaUrl, content.ctaOpensInNewTab ) }
				</div>
			</article>
		</div>
	</div>`;
}

/**
 * @param {Object} card Card modal data.
 * @return {string} Modal body HTML.
 */
export function renderModalBodyHtml( card ) {
	if ( ! card ) {
		return '';
	}

	if ( card.modalType === MODAL_TYPES.CAPITAL ) {
		return renderCapitalModalBody( card );
	}

	return renderFundModalBody( card );
}
